import type {
  SceneCatalogEntry,
  SceneSelectionLabels
} from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

/**
 * Scene IDs for black-and-white scenes.
 * These scenes have visibility rules for Simple and Standard sessions.
 */
export const BW_SCENE_IDS = ['shadowland', 'exposure']

/**
 * CMS fallback — SceneSelectionLabels (flat string fields).
 * Shared UI labels for scene selection components. Do not vary by session type.
 */
export const sceneSelectionLabelsFallback: SceneSelectionLabels = {
  internalName: 'Scene Selection: Labels',
  selectedBadge: 'SELECTED',
  availableBadge: 'AVAILABLE',
  lockedBadge: 'LOCKED',
  upgradeLabel: 'UPGRADE',
  confirmCtaLabel: 'Confirm',
  resetLabel: 'Reset',
  requiredScenesLabel: 'Required Scenes',
  cardIncludedBadge: 'INCLUDED',
  cardUpgradeBadge: 'UPGRADE',
  cardStudioOnlyBadge: 'STUDIO ONLY',
  cardSelectedBadgeSuffix: 'SELECTED',
  footerSelectDesktop: 'Select',
  footerSelectMobile: 'Pick',
  footerSceneSuffix: 'Scene(s)',
  footerContinueButton: 'Continue',
  footerReadyLabel: 'Ready!',
  groupPickPrefix: 'Pick',
  groupPickSuffixMobile: 'More',
  groupPickSuffixDesktop: 'More Scene(s) to Continue',
  groupUpgradeMaxedOutLabel: 'Upgrade for more scenes?'
}

/**
 * Master scene catalog — all available scenes across all groups.
 * Display content (name, image, description) for each scene; keyed by
 * `identifier` that is stable across the app. The `sceneContentRegistry`
 * maps API identifiers to these IDs.
 */
export const sceneCatalogFallback: SceneCatalogEntry[] = [
  // Studio Scenes
  {
    internalName: 'Scene: Yearbook',
    identifier: 'yearbook',
    name: 'Yearbook',
    image: { assetId: '', alt: 'Yearbook', url: '/content-images/webp/scene-selection/0001.webp' },
    description: 'Cap off your last year of high school with your final yearbook photo.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Center Stage',
    identifier: 'center-stage',
    name: 'Center Stage',
    image: {
      assetId: '',
      alt: 'Center Stage',
      url: '/content-images/webp/scene-selection/0002.webp'
    },
    description: 'A bright white backdrop that helps your favorite outfit pop.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: WonderLight',
    identifier: 'wonderlight',
    name: 'WonderLight',
    image: {
      assetId: '',
      alt: 'WonderLight',
      url: '/content-images/webp/scene-selection/0003.webp'
    },
    description: 'Saturated, colorful portraits, perfect for a unique look.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Prime Time',
    identifier: 'prime-time',
    name: 'Prime Time',
    image: {
      assetId: '',
      alt: 'Prime Time',
      url: '/content-images/webp/scene-selection/0004.webp'
    },
    description: 'Capture your favorite hobby with stunning lighting.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Shadowland',
    identifier: 'shadowland',
    name: 'Shadowland',
    image: {
      assetId: '',
      alt: 'Shadowland',
      url: '/content-images/webp/scene-selection/0005.webp'
    },
    description: 'A high contrast scene using shadows as texture.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Timeless',
    identifier: 'timeless',
    name: 'Timeless',
    image: {
      assetId: '',
      alt: 'Timeless',
      url: '/content-images/webp/scene-selection/0006.webp'
    },
    description: 'A charming rustic scene featuring exposed brick.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Emerge',
    identifier: 'emerge',
    name: 'Emerge',
    image: { assetId: '', alt: 'Emerge', url: '/content-images/webp/scene-selection/0007.webp' },
    description: 'Stand out with dramatic lighting against a moody backdrop.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Living Walls',
    identifier: 'living-walls',
    name: 'Living Walls',
    image: {
      assetId: '',
      alt: 'Living Walls',
      url: '/content-images/webp/scene-selection/0008.webp'
    },
    description: 'Featuring all kinds of plants for a green, modern look.',
    sceneType: 'studio'
  },
  {
    internalName: 'Scene: Exposure',
    identifier: 'exposure',
    name: 'Exposure',
    image: {
      assetId: '',
      alt: 'Exposure',
      url: '/content-images/webp/scene-selection/0009.webp'
    },
    description: 'Dramatic, high contrast, black-and-white portraits.',
    sceneType: 'studio'
  },
  // Outdoor Destinations
  {
    internalName: 'Scene: Outdoor',
    identifier: 'outdoor',
    name: 'Outdoor',
    image: { assetId: '', alt: 'Outdoor', url: '/content-images/webp/scene-selection/0010.webp' },
    description: 'Step outside the studio for the perfect outdoor lighting.',
    sceneType: 'outdoor'
  },
  {
    internalName: 'Scene: City',
    identifier: 'city',
    name: 'City',
    image: { assetId: '', alt: 'City', url: '/content-images/webp/scene-selection/0011.webp' },
    description: 'Pose in urban landscapes outside the studio.',
    sceneType: 'outdoor'
  },
  {
    internalName: 'Scene: Rustic',
    identifier: 'rustic',
    name: 'Rustic',
    image: { assetId: '', alt: 'Rustic', url: '/content-images/webp/scene-selection/0012.webp' },
    description: 'Country inspired setting, with natural surroundings.',
    sceneType: 'outdoor'
  },
  // Elite Unlimited Destinations
  {
    internalName: 'Scene: Choose Your Destination',
    identifier: 'choose-your-destination',
    name: 'Choose Your Destination',
    image: {
      assetId: '',
      alt: 'Choose Your Destination',
      url: '/content-images/webp/scene-selection/0013.webp'
    },
    description: 'Choose your own destination within 45 minutes of the studio.',
    sceneType: 'destination'
  },
  {
    internalName: 'Scene: Lake',
    identifier: 'lake',
    name: 'Lake',
    image: { assetId: '', alt: 'Lake', url: '/content-images/webp/scene-selection/0014.webp' },
    description: 'Jump in, the water\u2019s great! Enjoy stunning portraits at the lake.',
    sceneType: 'destination'
  },
  {
    internalName: 'Scene: Beach',
    identifier: 'beach',
    name: 'Beach',
    image: { assetId: '', alt: 'Beach', url: '/content-images/webp/scene-selection/0016.webp' },
    description: 'Elevate your portraits with an ocean background.',
    sceneType: 'destination'
  },
  {
    internalName: 'Scene: Waterfall',
    identifier: 'waterfall',
    name: 'Waterfall',
    image: { assetId: '', alt: 'Waterfall', url: '/content-images/webp/scene-selection/0015.webp' },
    description: 'Make a statement with a stunning waterfall background.',
    sceneType: 'destination'
  }
]

/**
 * Scene catalog indexed by identifier for O(1) lookups.
 * Used by the scene content registry to map API identifiers to display content.
 */
export const sceneCatalogById: Record<string, SceneCatalogEntry> = Object.fromEntries(
  sceneCatalogFallback.map((scene) => [scene.identifier, scene])
)
